<script lang="ts">
  import { createEventDispatcher, onMount, onDestroy } from 'svelte'
  import { fade, fly } from 'svelte/transition'
  import { fileStore, documents } from '$lib/stores/fileStore'
  import { contentIndexStore } from '$lib/stores/contentIndexStore'
  import type { DocumentInfo } from '$lib/storage/types'
  import { get } from 'svelte/store'
  import { File, Plus, Search, X } from 'lucide-svelte'

  const dispatch = createEventDispatcher()

  let isOpen = $state(false)
  let searchQuery = $state('')
  let selectedIndex = $state(0)
  let searchResults = $state<any[]>([])
  let searchInputEl: HTMLInputElement | null = $state(null)
  let showCreateOption = $state(false)
  let modalEl: HTMLElement | null = $state(null)
  let resultsContainerEl: HTMLElement | null = $state(null)
  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  let isSearching = $state(false)
  let fallbackToSimpleSearch = $state(false)

  // Simple fallback search for when ContentIndex is not ready
  function simpleSearch(query: string): DocumentInfo[] {
    const docs = get(documents)
    
    if (!docs || docs.length === 0) {
      return []
    }
    
    const queryLower = query.toLowerCase()
    
    // Match only on filename (last part of the path)
    const results = docs.filter(doc => {
      // Get just the filename from the title or id
      const filename = doc.title || doc.id.split('/').pop() || ''
      const filenameLower = filename.toLowerCase()
      
      // Check if filename contains the query
      return filenameLower.includes(queryLower)
    })
    
    return results.slice(0, 10)
  }

  // Highlight matched text
  function highlightSearchTerm(text: string, query: string): string {
    if (!query) return text
    
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    return text.replace(regex, '<mark>$1</mark>')
  }

  // Perform search using ContentIndex or fallback
  async function performSearch(query: string) {
    if (!query) {
      searchResults = []
      showCreateOption = false
      selectedIndex = 0
      isSearching = false
      return
    }

    // Skip search for single character (too broad, performance issue)
    if (query.length === 1) {
      searchResults = []
      showCreateOption = true
      selectedIndex = 0
      isSearching = false
      return
    }

    isSearching = true
    
    try {
      // Try ContentIndex first if available
      const indexState = get(contentIndexStore)
      if (indexState.isInitialized) {
        try {
          const indexResults = await contentIndexStore.search(query, {
            fuzzy: true,
            limit: 10,
            includeContext: false,
            sortBy: 'relevance'
          })
          
          if (indexResults && indexResults.length > 0) {
            searchResults = indexResults.map(result => {
              const title = result.title || result.path?.split('/').pop() || 'Untitled'
              return {
                id: result.path || result.id,
                title: title,
                path: result.path || result.id,  // Full path for display
                preview: result.preview || result.context?.join(' ') || '',
                score: result.score || 1
              }
            })
            fallbackToSimpleSearch = false
          } else {
            // No ContentIndex results, use simple search
            fallbackToSimpleSearch = true
            const simpleResults = simpleSearch(query)
            
            searchResults = simpleResults.map(doc => ({
              id: doc.id,
              title: doc.title || doc.id.split('/').pop() || 'Untitled',
              path: doc.id,  // Full path for display
              preview: doc.preview,
              score: 0
            }))
          }
        } catch (indexError) {
          console.error('ContentIndex search failed:', indexError)
          fallbackToSimpleSearch = true
          const simpleResults = simpleSearch(query)
          searchResults = simpleResults.map(doc => ({
            id: doc.id,
            title: doc.title || doc.id.split('/').pop() || 'Untitled',
            path: doc.id,  // Full path for display
            preview: doc.preview,
            score: 0
          }))
        }
      } else {
        // ContentIndex not initialized, use simple search
        fallbackToSimpleSearch = true
        const simpleResults = simpleSearch(query)
        
        searchResults = simpleResults.map(doc => ({
          id: doc.id,
          title: doc.title || doc.id.split('/').pop() || 'Untitled',
          path: doc.id,  // Full path for display
          preview: doc.preview,
          score: 0
        }))
      }
      
      // Show create option only when no exact match
      const hasExactMatch = searchResults.some(result => 
        result.title.toLowerCase() === query.toLowerCase()
      )
      showCreateOption = query.trim() !== '' && !hasExactMatch
      selectedIndex = 0
    } catch (error) {
      console.error('Search failed:', error)
      searchResults = []
      showCreateOption = query.trim() !== ''
    } finally {
      isSearching = false
    }
  }

  // Debounced search effect with dynamic delay
  $effect(() => {
    // Explicitly read searchQuery to establish dependency
    const query = searchQuery
    
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }
    
    // Dynamic debounce: longer for short queries, shorter for specific queries
    let delay: number
    if (!query) {
      delay = 0 // Immediate clear
    } else if (query.length === 1) {
      delay = 500 // Long delay for single character (usually still typing)
    } else if (query.length === 2) {
      delay = 300 // Medium delay for 2 characters
    } else {
      delay = 150 // Fast response for 3+ characters (more specific search)
    }
    
    debounceTimer = setTimeout(() => {
      performSearch(query)
    }, delay)
    
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }
    }
  })

  // Auto-scroll to selected item when selectedIndex changes
  $effect(() => {
    if (resultsContainerEl && selectedIndex >= 0) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        const selectedElement = resultsContainerEl?.querySelector('.result-item.selected') as HTMLElement
        if (selectedElement) {
          selectedElement.scrollIntoView({
            block: 'nearest',
            behavior: 'smooth'
          })
        }
      }, 0)
    }
  })

  // Open the modal
  export async function open() {
    isOpen = true
    searchQuery = ''
    selectedIndex = 0
    searchResults = []
    showCreateOption = false
    fallbackToSimpleSearch = false
    
    // Ensure documents are loaded
    const docs = get(documents)
    if (!docs || docs.length === 0) {
      await fileStore.loadDocuments()
    }
    
    setTimeout(() => {
      searchInputEl?.focus()
    }, 50)
  }

  // Close the modal
  function close() {
    isOpen = false
    searchQuery = ''
    searchResults = []
    showCreateOption = false
    isSearching = false
  }

  // Handle file selection
  async function selectFile(result: any) {
    // Try to find document in store first
    const docs = get(documents)
    const doc = docs.find(d => d.id === result.id || d.id === result.path)
    
    if (doc) {
      dispatch('openFile', { file: doc })
    } else {
      // Create a minimal DocumentInfo object for opening
      const docInfo: DocumentInfo = {
        id: result.id || result.path,
        title: result.title,
        preview: result.preview || '',
        createdAt: new Date(),
        updatedAt: new Date(),
        wordCount: 0
      }
      dispatch('openFile', { file: docInfo })
    }
    close()
  }

  // Create new file
  function createNewFile() {
    if (searchQuery.trim()) {
      dispatch('createFile', { name: searchQuery.trim() })
      close()
    }
  }

  // Keyboard navigation in modal
  function handleModalKeydown(e: KeyboardEvent) {
    switch(e.key) {
      case 'Escape':
        e.preventDefault()
        e.stopPropagation()
        close()
        break
        
      case 'ArrowDown':
        e.preventDefault()
        e.stopPropagation()
        const totalItems = searchResults.length + (showCreateOption ? 1 : 0)
        if (totalItems > 0) {
          selectedIndex = (selectedIndex + 1) % totalItems
        }
        break
        
      case 'ArrowUp':
        e.preventDefault()
        e.stopPropagation()
        const total = searchResults.length + (showCreateOption ? 1 : 0)
        if (total > 0) {
          selectedIndex = (selectedIndex - 1 + total) % total
        }
        break
        
      case 'Enter':
        e.preventDefault()
        e.stopPropagation()
        
        // Shift+Enter always creates new file
        if (e.shiftKey && searchQuery.trim()) {
          createNewFile()
        } 
        // Regular Enter - open selected or create if it's the create option
        else {
          const isCreateOptionSelected = showCreateOption && selectedIndex === searchResults.length
          if (isCreateOptionSelected) {
            createNewFile()
          } else if (searchResults[selectedIndex]) {
            selectFile(searchResults[selectedIndex])
          }
        }
        break
    }
  }

  // Global keyboard shortcut (Cmd+O or Ctrl+O)
  function handleGlobalKeydown(e: KeyboardEvent) {
    if ((e.metaKey || e.ctrlKey) && e.key === 'o') {
      e.preventDefault()
      e.stopPropagation()
      open()
    }
  }

  onMount(async () => {
    window.addEventListener('keydown', handleGlobalKeydown)
    
    // Pre-load documents if vault is initialized
    try {
      const { vaultStore } = await import('$lib/stores/vaultStore')
      const vault = get(vaultStore)
      
      if (vault.isInitialized && vault.vaultPath) {
        const docs = get(documents)
        if (!docs || docs.length === 0) {
          await fileStore.loadDocuments()
        }
      }
    } catch (err) {
      // Silently fail
    }
  })

  onDestroy(() => {
    window.removeEventListener('keydown', handleGlobalKeydown)
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }
  })
</script>

{#if isOpen}
  <div 
    class="quick-open-backdrop"
    role="dialog"
    aria-modal="true"
    aria-labelledby="quick-open-title"
    tabindex="-1"
    onclick={close}
    onkeydown={(e) => e.key === 'Escape' && close()}
    transition:fade={{ duration: 150 }}
  >
    <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
    <div 
      bind:this={modalEl}
      class="quick-open-modal"
      role="search"
      onclick={(e) => e.stopPropagation()}
      onkeydown={handleModalKeydown}
      transition:fly={{ y: -20, duration: 200 }}
    >
      <div class="search-container">
        <Search size={16} class="search-icon" />
        <input
          bind:this={searchInputEl}
          bind:value={searchQuery}
          type="text"
          class="search-input"
          placeholder="Search or create a note..."
          autocomplete="off"
          spellcheck="false"
          aria-label="Search or create a note"
          id="quick-open-title"
        />
        {#if searchQuery}
          <button 
            class="clear-button" 
            onclick={() => searchQuery = ''}
            tabindex="-1"
          >
            <X size={16} />
          </button>
        {/if}
      </div>

      {#if searchQuery || isSearching}
        <!-- Force Svelte to track searchQuery changes -->
        <div bind:this={resultsContainerEl} class="results-container" data-query={searchQuery}>
          {#if isSearching}
            <div class="searching">
              Searching...
            </div>
          {:else if searchResults.length > 0}
            {#each searchResults as result, index}
              <button
                class="result-item"
                class:selected={index === selectedIndex}
                onclick={() => selectFile(result)}
                onmouseenter={() => selectedIndex = index}
                type="button"
              >
                <File size={16} class="icon" />
                <div class="result-content">
                  <div class="result-title">
                    {@html highlightSearchTerm(result.title, searchQuery)}
                  </div>
                  {#if result.path && result.path !== result.title}
                    <div class="result-path">{result.path}</div>
                  {/if}
                </div>
              </button>
            {/each}
          {/if}
          
          {#if showCreateOption && !isSearching}
            <button
              class="result-item"
              class:selected={selectedIndex === searchResults.length}
              onclick={createNewFile}
              onmouseenter={() => selectedIndex = searchResults.length}
              type="button"
            >
              <Plus size={16} class="icon" />
              <span class="result-text">{searchQuery}</span>
              <span class="create-hint">Enter to create</span>
            </button>
          {/if}
          
          {#if searchQuery.length === 1}
            <div class="no-results">
              Continue typing to search...
            </div>
          {:else if searchResults.length === 0 && !showCreateOption && !isSearching && searchQuery}
            <div class="no-results">
              No matching notes found
            </div>
          {/if}
        </div>
      {/if}

      <div class="hints-bar">
        <span class="hint">
          <kbd>↑↓</kbd> Navigate
        </span>
        <span class="hint-separator">|</span>
        <span class="hint">
          <kbd>Enter</kbd> Open
        </span>
        <span class="hint-separator">|</span>
        <span class="hint">
          <kbd>Shift Enter</kbd> Create
        </span>
        <span class="hint-separator">|</span>
        <span class="hint">
          <kbd>Esc</kbd> Close
        </span>
        {#if fallbackToSimpleSearch}
          <span class="hint-separator">|</span>
          <span class="hint fallback">Simple search</span>
        {/if}
      </div>
    </div>
  </div>
{/if}

<style>
  .quick-open-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 999999;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 120px;
  }

  .quick-open-modal {
    background: var(--bg-primary);
    border: 1px solid var(--border-primary);
    border-radius: 8px;
    box-shadow: 
      0 2px 8px rgba(0, 0, 0, 0.1),
      0 8px 24px rgba(0, 0, 0, 0.15);
    width: 600px;
    max-width: 90vw;
    display: flex;
    flex-direction: column;
  }

  .search-container {
    display: flex;
    align-items: center;
    padding: 12px;
    border-bottom: 1px solid var(--border-primary);
    position: relative;
  }

  :global(.search-icon) {
    position: absolute;
    left: 24px;
    color: var(--text-secondary);
    pointer-events: none;
  }

  .search-input {
    flex: 1;
    padding: 8px 36px 8px 44px;
    font-size: 16px;
    background: transparent;
    border: none;
    color: var(--text-primary);
    outline: none;
  }

  .search-input::placeholder {
    color: var(--text-secondary);
  }

  .clear-button {
    position: absolute;
    right: 20px;
    background: none;
    border: none;
    color: var(--text-secondary);
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.6;
    transition: opacity 0.1s;
  }

  .clear-button:hover {
    opacity: 1;
  }

  .results-container {
    flex: 1;
    overflow-y: auto;
    padding: 4px;
    max-height: 320px;
    min-height: 0;
  }

  .searching {
    padding: 32px;
    text-align: center;
    color: var(--text-secondary);
    font-size: 14px;
  }

  .result-item {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    margin: 2px 4px;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.1s;
    gap: 12px;
    width: 100%;
    border: none;
    background: transparent;
    text-align: left;
    font-family: inherit;
    font-size: inherit;
  }

  .result-item:hover {
    background: var(--bg-tertiary);
  }

  .result-item.selected {
    background: var(--accent-primary);
    color: white;
  }

  .result-item.selected :global(.icon),
  .result-item.selected .result-title,
  .result-item.selected .result-text,
  .result-item.selected .result-path,
  .result-item.selected .create-hint {
    color: white;
    opacity: 1;
  }

  :global(.icon) {
    flex-shrink: 0;
    color: var(--text-secondary);
  }

  .result-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .result-title {
    color: var(--text-primary);
    font-size: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .result-text {
    color: var(--text-primary);
    font-size: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .result-path {
    color: var(--text-secondary);
    font-size: 11px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    opacity: 0.8;
  }

  :global(.result-title mark),
  :global(.result-text mark) {
    background: transparent;
    color: var(--accent-primary);
    font-weight: 600;
  }

  :global(.result-item.selected .result-title mark),
  :global(.result-item.selected .result-text mark) {
    color: white;
    text-decoration: underline;
  }
  
  .create-hint {
    font-size: 12px;
    color: var(--accent-primary);
    margin-left: auto;
    padding-right: 4px;
  }

  .no-results {
    padding: 32px;
    text-align: center;
    color: var(--text-secondary);
    font-size: 14px;
  }

  .hints-bar {
    padding: 6px 16px;
    background: var(--bg-primary);
    border-top: 1px solid var(--border-primary);
    display: flex;
    align-items: center;
    gap: 8px;
    border-radius: 0 0 8px 8px;
    flex-shrink: 0;
  }

  .hint {
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--text-secondary);
    font-size: 11px;
  }

  .hint-separator {
    color: var(--border-primary);
    font-size: 11px;
    padding: 0 4px;
  }

  .hint.fallback {
    color: var(--text-tertiary);
    font-style: italic;
  }

  .hint kbd {
    padding: 1px 3px;
    background: var(--bg-secondary);
    border: 1px solid var(--border-primary);
    border-radius: 3px;
    font-size: 10px;
    font-family: monospace;
    min-width: 18px;
    text-align: center;
  }
</style>